<template>
  <div class="section-export">
    <p class="section-export-label">Export</p>
    <p class="section-export-copy">
      Trim this moment, preview it, then prepare an MP4.
    </p>
    <button
      ref="exportTrigger"
      type="button"
      class="section-export-button"
      :disabled="!canOpenExport"
      @click="openModal"
    >
      {{ triggerLabel }}
    </button>

    <div
      v-if="modalOpen"
      class="export-modal-backdrop"
      @mousedown.self="closeModal"
    >
      <section
        ref="dialog"
        class="export-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-modal-title"
        tabindex="-1"
        @keydown="onModalKeydown"
      >
        <header class="export-modal-header">
          <div>
            <h2 id="export-modal-title">{{ modalTitle }}</h2>
            <p class="export-modal-subtitle">{{ clipEvent.title || 'Ceremony moment' }}</p>
          </div>
          <button
            type="button"
            class="modal-close"
            aria-label="Close export window"
            @click="closeModal"
          >
            ×
          </button>
        </header>

        <template v-if="modalStatus === 'editing'">
          <div class="export-modal-body">
            <div class="export-preview">
              <ceremony-media-player
                ref="exportMediaPlayer"
                :active-video-url="activeVideoUrl"
                :video-options="trimVideoOptions"
                readiness="ready"
                @loadeddata="onModalPlayerLoaded"
              />
            </div>

            <div class="playback-summary" aria-live="polite">
              <button type="button" class="preview-button" @click="playSelection">
                <span aria-hidden="true">{{ isPreviewPlaying ? '❚❚' : '▶' }}</span>
                {{ isPreviewPlaying ? 'Pause' : 'Play clip' }}
              </button>
              <p class="clip-readout">
                <strong>{{ formatClipDuration(sectionDurationSeconds) }}</strong>
                <span>
                  {{ formatTimecode(draftStartSeconds) }}–{{ formatTimecode(draftEndSeconds) }}
                </span>
              </p>
            </div>

            <div
              ref="filmstrip"
              class="trim-filmstrip"
              aria-label="Clip filmstrip"
              @click="seekFromFilmstrip"
            >
              <div
                class="filmstrip-frames"
                :class="{ loading: filmstripLoading }"
                aria-hidden="true"
              >
                <span
                  v-for="frame in filmstripFrames"
                  :key="`${frame.timestamp_seconds}-${frame.box.join('-')}`"
                  class="filmstrip-frame"
                >
                  <span
                    class="filmstrip-frame-image"
                    :style="filmstripFrameStyle(frame)"
                  ></span>
                </span>
              </div>
              <p
                v-if="!filmstripLoading && !filmstripFrames.length"
                class="filmstrip-empty"
                aria-live="polite"
              >
                Preview frames unavailable
              </p>
              <span
                v-for="marker in visibleCutMarkers"
                :key="marker.id"
                class="cut-marker"
                :style="marker.style"
                aria-hidden="true"
              ></span>
              <span class="filmstrip-mask filmstrip-mask-left" :style="leftMaskStyle"></span>
              <span class="filmstrip-mask filmstrip-mask-right" :style="rightMaskStyle"></span>
              <span
                class="trim-selection"
                :class="{
                  'focus-start': focusedControl === 'start',
                  'focus-end': focusedControl === 'end',
                  'focus-selection': focusedControl === 'selection',
                  dragging: Boolean(selectionDrag),
                }"
                :style="timelineSelectionStyle"
              >
                <span class="trim-handle trim-handle-start" aria-hidden="true"></span>
                <button
                  type="button"
                  class="selection-drag-target"
                  aria-label="Move clip selection"
                  @click.stop.prevent
                  @pointerdown="startSelectionDrag"
                  @pointermove="moveSelectionDrag"
                  @pointerup="finishSelectionDrag"
                  @pointercancel="finishSelectionDrag"
                  @keydown="onSelectionKeydown"
                  @focus="focusedControl = 'selection'"
                  @blur="focusedControl = ''"
                >
                  <span class="selection-grip" aria-hidden="true">
                    <svg viewBox="0 0 16 16" fill="none">
                      <path d="M2 8h12M5 5 2 8l3 3M11 5l3 3-3 3" />
                    </svg>
                  </span>
                </button>
                <span class="trim-handle trim-handle-end" aria-hidden="true"></span>
              </span>
              <span
                v-if="!playheadOnBoundary"
                class="filmstrip-playhead"
                :class="{ focused: focusedControl === 'playhead' }"
                :style="playheadStyle"
                aria-hidden="true"
              ></span>
              <input
                class="playhead-range"
                type="range"
                :min="timelineStartSeconds"
                :max="timelineEndSeconds"
                step="1"
                :value="playheadSeconds"
                aria-label="Preview position"
                @input="setPlayhead($event.target.value)"
                @focus="focusedControl = 'playhead'"
                @blur="focusedControl = ''"
              >
              <input
                class="trim-range trim-range-start"
                type="range"
                :min="timelineStartSeconds"
                :max="timelineEndSeconds"
                step="1"
                :value="draftStartSeconds"
                aria-label="Clip start"
                @input="setDraftStartFromRail"
                @keydown="onHandleKeydown($event, 'start')"
                @focus="focusedControl = 'start'"
                @blur="focusedControl = ''"
              >
              <input
                class="trim-range trim-range-end"
                type="range"
                :min="timelineStartSeconds"
                :max="timelineEndSeconds"
                step="1"
                :value="draftEndSeconds"
                aria-label="Clip end"
                @input="setDraftEndFromRail"
                @keydown="onHandleKeydown($event, 'end')"
                @focus="focusedControl = 'end'"
                @blur="focusedControl = ''"
              >
            </div>

            <div class="filmstrip-scale">
              <time>{{ formatTimecode(timelineStartSeconds) }}</time>
              <span v-if="snapMessage" class="snap-message" role="status">
                {{ snapMessage }}
              </span>
              <span v-else class="trim-hint">Drag edges to trim · ↔ moves clip</span>
              <time>{{ formatTimecode(timelineEndSeconds) }}</time>
            </div>

            <div class="ceremony-overview" aria-label="Position within the whole ceremony">
              <div class="ceremony-overview-track" aria-hidden="true">
                <span class="ceremony-overview-window" :style="overviewWindowStyle"></span>
              </div>
              <div class="ceremony-overview-scale">
                <time>00:00</time>
                <span>Position in ceremony</span>
                <time>{{ formatTimecode(sourceDurationSeconds) }}</time>
              </div>
            </div>

            <div v-if="hasDraftChanges" class="trim-toolbar">
              <button
                v-if="hasDraftChanges"
                type="button"
                class="reset-button"
                @click="resetDraft"
              >
                Reset
              </button>
            </div>

            <details class="fine-tune">
              <summary>Exact times</summary>
              <button
                v-if="hasCutSnapPoints"
                type="button"
                class="magnet-button"
                :class="{ active: snapEnabled }"
                :aria-pressed="String(snapEnabled)"
                @click="snapEnabled = !snapEnabled"
              >
                <svg
                  class="magnet-icon"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M3 2v6a5 5 0 0 0 10 0V2" />
                  <path d="M3 5h3M10 5h3" />
                </svg>
                Snap to camera cuts
              </button>
              <div class="trim-controls">
                <fieldset class="time-control">
                  <legend>Start</legend>
                  <div class="time-input-row">
                    <button type="button" aria-label="Start one second earlier" @click="nudgeStart(-1)">
                      −1s
                    </button>
                    <input
                      v-model="startInput"
                      type="text"
                      inputmode="numeric"
                      aria-label="Clip start time"
                      @change="commitStartInput"
                      @focus="$event.target.select()"
                    >
                    <button type="button" aria-label="Start one second later" @click="nudgeStart(1)">
                      +1s
                    </button>
                  </div>
                </fieldset>

                <fieldset class="time-control">
                  <legend>End</legend>
                  <div class="time-input-row">
                    <button type="button" aria-label="End one second earlier" @click="nudgeEnd(-1)">
                      −1s
                    </button>
                    <input
                      v-model="endInput"
                      type="text"
                      inputmode="numeric"
                      aria-label="Clip end time"
                      @change="commitEndInput"
                      @focus="$event.target.select()"
                    >
                    <button type="button" aria-label="End one second later" @click="nudgeEnd(1)">
                      +1s
                    </button>
                  </div>
                </fieldset>
              </div>
            </details>

            <p v-if="validationError" class="section-export-error" role="alert">
              {{ validationError }}
            </p>
          </div>

          <footer class="export-modal-footer">
            <button type="button" class="footer-secondary" @click="closeModal">
              Cancel
            </button>
            <button
              type="button"
              class="footer-primary"
              :disabled="!canExportSection"
              @click="exportEvent"
            >
              Prepare clip
            </button>
          </footer>
        </template>

        <div v-else-if="modalStatus === 'pending'" class="export-state" aria-live="polite">
          <span class="export-progress" aria-hidden="true"></span>
          <p>
            This usually takes a few minutes. You can close this window and return
            while it continues.
          </p>
          <div class="export-range-card">
            <strong>{{ formatTimecode(sectionDurationSeconds) }} clip</strong>
            <span>{{ formatTimecode(draftStartSeconds) }}–{{ formatTimecode(draftEndSeconds) }}</span>
          </div>
          <button type="button" class="footer-secondary" @click="closeModal">Close</button>
        </div>

        <div v-else-if="modalStatus === 'ready'" class="export-state export-ready" aria-live="polite">
          <span class="export-success" aria-hidden="true">✓</span>
          <p>We’ll refresh the secure link when you download.</p>
          <p v-if="exportState.download_error" class="section-export-error" role="alert">
            {{ exportState.download_error }}
          </p>
          <div class="export-range-card">
            <strong>{{ clipEvent.title || 'Ceremony clip' }}</strong>
            <span>
              {{ formatTimecode(draftStartSeconds) }}–{{ formatTimecode(draftEndSeconds) }}
              · {{ formatTimecode(sectionDurationSeconds) }}
            </span>
          </div>
          <div class="export-state-actions">
            <button
              type="button"
              class="footer-primary download-button"
              @click="$emit('download-export', {
                exportKey: currentExportKey,
                exportId: exportState.export_id,
                eventId: clipEvent.id,
              })"
            >
              Download MP4
            </button>
            <button type="button" class="footer-secondary" @click="editClip">Edit clip</button>
            <button type="button" class="footer-secondary" @click="closeModal">Close</button>
          </div>
        </div>

        <div v-else class="export-state export-failed" role="alert">
          <span class="export-failure" aria-hidden="true">!</span>
          <h3>We couldn’t prepare this clip</h3>
          <p>{{ exportError }}</p>
          <p class="export-preserved">Your trim has been saved.</p>
          <div class="export-state-actions">
            <button type="button" class="footer-primary" @click="exportEvent">Try again</button>
            <button type="button" class="footer-secondary" @click="editClip">Edit clip</button>
            <button type="button" class="footer-secondary" @click="closeModal">Close</button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  MAX_SECTION_EXPORT_SECONDS,
  getCeremonyFilmstrip,
  isSectionExportPending,
  sectionExportRequestKey,
} from '../../../api/sectionExports.js'
import CeremonyMediaPlayer from './CeremonyMediaPlayer.vue'

const MIN_SECTION_EXPORT_SECONDS = 1
const SNAP_THRESHOLD_SECONDS = 1

function eventRange(event) {
  const start = Number(
    event.cluster_start_sec ?? event.windowStartSeconds ?? event.timestamp
  )
  const end = Number(event.cluster_end_sec ?? event.windowEndSeconds)
  return {
    start: Number.isFinite(start) ? Math.max(0, Math.floor(start)) : 0,
    end: Number.isFinite(end) ? Math.ceil(end) : 0,
  }
}

function parseTimecode(value) {
  const parts = String(value || '').trim().split(':')
  if (!parts.length || parts.length > 3 || parts.some(part => !/^\d+$/.test(part))) {
    return null
  }
  return parts.reduce((total, part) => (total * 60) + Number(part), 0)
}

export default {
  name: 'CeremonySectionExport',
  components: {
    CeremonyMediaPlayer,
  },
  props: {
    activeEvent: {
      type: Object,
      required: true,
    },
    ceremonyId: {
      type: String,
      required: true,
    },
    exportStates: {
      type: Object,
      default: () => ({}),
    },
    formatDuration: {
      type: Function,
      required: true,
    },
    activeVideoUrl: {
      type: String,
      default: '',
    },
    videoOptions: {
      type: Object,
      required: true,
    },
    ceremonyVideoDuration: {
      type: Number,
      default: 0,
    },
    cutTimes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      modalOpen: false,
      clipEvent: {},
      snapshotCeremonyId: '',
      suggestedStartSeconds: 0,
      suggestedEndSeconds: 1,
      draftStartSeconds: 0,
      draftEndSeconds: 1,
      startInput: '00:00',
      endInput: '00:01',
      inputError: '',
      forceEditing: false,
      submittedStartSeconds: null,
      submittedEndSeconds: null,
      previousBodyOverflow: '',
      playheadSeconds: 0,
      isPreviewPlaying: false,
      snapEnabled: true,
      snapMessage: '',
      focusedControl: '',
      filmstripFrames: [],
      filmstripLoading: false,
      filmstripRequestId: 0,
      compactTimeline: false,
      timelineWindowStart: 0,
      timelineWindowEnd: 1,
      selectionDrag: null,
    }
  },
  computed: {
    activeRequestKey() {
      const range = eventRange(this.activeEvent)
      if (!this.activeEvent.id || range.end <= range.start) return ''
      return sectionExportRequestKey({
        ceremonyId: this.activeEvent.ceremonyId || this.ceremonyId,
        sectionId: this.activeEvent.id,
        startSeconds: range.start,
        endSeconds: range.end,
      })
    },
    activeExportState() {
      return this.exportStates[this.activeRequestKey] || {}
    },
    lastActiveExportKey() {
      const ceremonyId = String(this.activeEvent.ceremonyId || this.ceremonyId)
      const sectionId = String(this.activeEvent.id || '')
      return Object.keys(this.exportStates).reverse().find(exportKey => {
        const request = this.exportStates[exportKey]?.request
        return (
          String(request?.ceremonyId || '') === ceremonyId
          && String(request?.sectionId || '') === sectionId
        )
      }) || ''
    },
    triggerExportState() {
      return this.exportStates[this.lastActiveExportKey]
        || this.activeExportState
        || {}
    },
    currentExportKey() {
      if (!this.clipEvent.id || !this.snapshotCeremonyId) return ''
      return sectionExportRequestKey({
        ceremonyId: this.snapshotCeremonyId,
        sectionId: this.clipEvent.id,
        startSeconds: this.draftStartSeconds,
        endSeconds: this.draftEndSeconds,
      })
    },
    exportState() {
      return this.exportStates[this.currentExportKey] || {}
    },
    canOpenExport() {
      const range = eventRange(this.activeEvent)
      return Boolean(this.activeVideoUrl && range.end > range.start)
    },
    triggerLabel() {
      if (isSectionExportPending(this.triggerExportState)) return 'Preparing clip…'
      if (
        String(this.triggerExportState.status || '').toUpperCase() === 'COMPLETE'
        && this.triggerExportState.download_url
      ) return 'Download clip'
      return 'Export clip'
    },
    sourceDurationSeconds() {
      const videoDuration = Math.floor(Number(this.ceremonyVideoDuration) || 0)
      return Math.max(1, videoDuration, this.suggestedEndSeconds)
    },
    trimVideoOptions() {
      return {
        ...this.videoOptions,
        autoplay: false,
        controls: false,
        controlBar: false,
        bigPlayButton: false,
      }
    },
    sectionDurationSeconds() {
      return this.draftEndSeconds - this.draftStartSeconds
    },
    hasDraftChanges() {
      return (
        this.draftStartSeconds !== this.suggestedStartSeconds
        || this.draftEndSeconds !== this.suggestedEndSeconds
      )
    },
    validationError() {
      if (this.inputError) return this.inputError
      if (this.draftStartSeconds < 0) return 'The clip must start at or after 00:00.'
      if (this.draftEndSeconds <= this.draftStartSeconds) {
        return 'The end must be after the start.'
      }
      if (this.sectionDurationSeconds < MIN_SECTION_EXPORT_SECONDS) {
        return 'The clip must be at least one second.'
      }
      if (this.sectionDurationSeconds > MAX_SECTION_EXPORT_SECONDS) {
        return `The clip cannot be longer than ${this.formatTimecode(MAX_SECTION_EXPORT_SECONDS)}.`
      }
      if (this.draftEndSeconds > this.sourceDurationSeconds) {
        return 'The clip cannot end after the ceremony video.'
      }
      return ''
    },
    canExportSection() {
      return !this.validationError && !isSectionExportPending(this.exportState)
    },
    exportRangeStart() {
      const value = Number(this.exportState.start_seconds)
      return Number.isFinite(value) ? value : this.submittedStartSeconds
    },
    exportRangeEnd() {
      const value = Number(this.exportState.end_seconds)
      return Number.isFinite(value) ? value : this.submittedEndSeconds
    },
    exportMatchesDraft() {
      return (
        Number.isFinite(this.exportRangeStart)
        && Number.isFinite(this.exportRangeEnd)
        && this.exportRangeStart === this.draftStartSeconds
        && this.exportRangeEnd === this.draftEndSeconds
      )
    },
    exportStatus() {
      return String(this.exportState.status || '').toUpperCase()
    },
    modalStatus() {
      if (this.forceEditing || !this.exportMatchesDraft) return 'editing'
      if (isSectionExportPending(this.exportState)) return 'pending'
      if (this.exportStatus === 'COMPLETE' && this.exportState.download_url) return 'ready'
      if (['ERROR', 'CANCELED'].includes(this.exportStatus)) return 'error'
      return 'editing'
    },
    modalTitle() {
      const titles = {
        editing: 'Trim clip',
        pending: 'Preparing your clip',
        ready: 'Your clip is ready',
        error: 'Export needs attention',
      }
      return titles[this.modalStatus]
    },
    exportError() {
      return this.exportState.error || 'The clip could not be prepared. Please try again.'
    },
    timelineStartSeconds() {
      return this.timelineWindowStart
    },
    timelineEndSeconds() {
      return this.timelineWindowEnd
    },
    timelinePercentages() {
      const span = Math.max(1, this.timelineEndSeconds - this.timelineStartSeconds)
      const left = ((this.draftStartSeconds - this.timelineStartSeconds) / span) * 100
      const right = ((this.draftEndSeconds - this.timelineStartSeconds) / span) * 100
      return {
        left: Math.max(0, Math.min(100, left)),
        right: Math.max(0, Math.min(100, right)),
      }
    },
    timelineSelectionStyle() {
      return {
        left: `${this.timelinePercentages.left}%`,
        width: `${this.timelinePercentages.right - this.timelinePercentages.left}%`,
      }
    },
    leftMaskStyle() {
      return { width: `${this.timelinePercentages.left}%` }
    },
    rightMaskStyle() {
      return { left: `${this.timelinePercentages.right}%` }
    },
    playheadStyle() {
      const span = Math.max(1, this.timelineEndSeconds - this.timelineStartSeconds)
      const percent = ((this.playheadSeconds - this.timelineStartSeconds) / span) * 100
      return { left: `${Math.max(0, Math.min(100, percent))}%` }
    },
    playheadOnBoundary() {
      return (
        Math.abs(this.playheadSeconds - this.draftStartSeconds) < 0.5
        || Math.abs(this.playheadSeconds - this.draftEndSeconds) < 0.5
      )
    },
    overviewWindowStyle() {
      const sourceSpan = Math.max(1, this.sourceDurationSeconds)
      const actualWidth = ((this.timelineEndSeconds - this.timelineStartSeconds) / sourceSpan) * 100
      const width = Math.max(2, Math.min(100, actualWidth))
      const desiredLeft = (this.timelineStartSeconds / sourceSpan) * 100
      return {
        left: `${Math.min(100 - width, desiredLeft)}%`,
        width: `${width}%`,
      }
    },
    visibleCutTimes() {
      return [...new Set(this.cutTimes
        .map(value => Math.round(Number(value)))
        .filter(value => (
          Number.isFinite(value)
          && value >= this.timelineStartSeconds
          && value <= this.timelineEndSeconds
        )))]
    },
    hasCutSnapPoints() {
      return this.visibleCutTimes.length > 0
    },
    visibleCutMarkers() {
      const span = Math.max(1, this.timelineEndSeconds - this.timelineStartSeconds)
      return this.visibleCutTimes.map((timestamp, index) => ({
        id: `${timestamp}-${index}`,
        style: {
          left: `${((timestamp - this.timelineStartSeconds) / span) * 100}%`,
        },
      }))
    },
  },
  watch: {
    'activeEvent.id'() {
      if (this.modalOpen) this.closeModal()
    },
    ceremonyId() {
      if (this.modalOpen) this.closeModal()
    },
  },
  mounted() {
    this.updateCompactTimeline()
    window.addEventListener('resize', this.updateCompactTimeline)
  },
  beforeDestroy() {
    this.stopPreview()
    this.restoreBodyScroll()
    window.removeEventListener('resize', this.updateCompactTimeline)
  },
  methods: {
    formatTimecode(totalSeconds) {
      const safeSeconds = Math.max(0, Math.round(Number(totalSeconds) || 0))
      const hours = Math.floor(safeSeconds / 3600)
      const minutes = Math.floor((safeSeconds % 3600) / 60)
      const seconds = safeSeconds % 60
      if (hours) {
        return [hours, minutes, seconds]
          .map((part, index) => String(part).padStart(index === 0 ? 1 : 2, '0'))
          .join(':')
      }
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    formatClipDuration(totalSeconds) {
      const safeSeconds = Math.max(0, Math.round(Number(totalSeconds) || 0))
      if (safeSeconds < 60) return `${safeSeconds} sec`
      return `${this.formatTimecode(safeSeconds)} clip`
    },
    updateCompactTimeline() {
      const compact = window.matchMedia('(max-width: 640px)').matches
      if (compact === this.compactTimeline) return
      this.compactTimeline = compact
      if (this.modalOpen) this.reframeTimeline()
    },
    calculateTimelineBounds() {
      const context = this.compactTimeline ? 8 : 12
      const anchorStart = Math.min(this.draftStartSeconds, this.suggestedStartSeconds)
      const anchorEnd = Math.max(this.draftEndSeconds, this.suggestedEndSeconds)
      const desiredSpan = Math.max(10, (anchorEnd - anchorStart) + (context * 2))
      let start = Math.max(0, Math.floor(anchorStart - context))
      let end = Math.min(this.sourceDurationSeconds, Math.ceil(anchorEnd + context))

      if (start === 0) end = Math.min(this.sourceDurationSeconds, desiredSpan)
      if (end === this.sourceDurationSeconds) {
        start = Math.max(0, this.sourceDurationSeconds - desiredSpan)
      }
      return { start, end: Math.max(start + 1, end) }
    },
    reframeTimeline(loadFrames = true) {
      const { start, end } = this.calculateTimelineBounds()
      const changed = start !== this.timelineWindowStart || end !== this.timelineWindowEnd
      this.timelineWindowStart = start
      this.timelineWindowEnd = end
      if (changed && loadFrames && this.modalOpen) {
        this.$nextTick(() => this.loadFilmstrip())
      }
    },
    reframeIfDraftOutsideTimeline() {
      if (
        this.draftStartSeconds < this.timelineWindowStart
        || this.draftEndSeconds > this.timelineWindowEnd
      ) {
        this.reframeTimeline()
      }
    },
    openModal() {
      if (!this.canOpenExport) return
      this.$emit('modal-open')
      this.clipEvent = { ...this.activeEvent }
      this.snapshotCeremonyId = String(
        this.clipEvent.ceremonyId || this.ceremonyId
      )
      const suggested = eventRange(this.clipEvent)
      this.suggestedStartSeconds = suggested.start
      this.suggestedEndSeconds = Math.max(suggested.start + 1, suggested.end)
      const stateStart = Number(this.triggerExportState.start_seconds)
      const stateEnd = Number(this.triggerExportState.end_seconds)
      const hasStateRange = Number.isFinite(stateStart) && Number.isFinite(stateEnd)
      this.draftStartSeconds = hasStateRange ? stateStart : this.suggestedStartSeconds
      this.draftEndSeconds = hasStateRange ? stateEnd : this.suggestedEndSeconds
      this.updateCompactTimeline()
      this.reframeTimeline(false)
      this.playheadSeconds = this.draftStartSeconds
      this.isPreviewPlaying = false
      this.snapEnabled = true
      this.snapMessage = ''
      this.syncTimeInputs()
      this.inputError = ''
      this.forceEditing = false
      this.modalOpen = true
      this.previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      this.$nextTick(() => {
        this.$refs.dialog?.focus()
        if (this.modalStatus === 'editing') this.loadFilmstrip()
      })
    },
    closeModal() {
      this.stopPreview()
      this.filmstripRequestId += 1
      this.filmstripLoading = false
      this.modalOpen = false
      this.restoreBodyScroll()
      this.$nextTick(() => this.$refs.exportTrigger?.focus())
    },
    restoreBodyScroll() {
      if (!this.modalOpen && !this.previousBodyOverflow && document.body.style.overflow !== 'hidden') {
        return
      }
      document.body.style.overflow = this.previousBodyOverflow
      this.previousBodyOverflow = ''
    },
    onModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeModal()
        return
      }
      if (
        event.code === 'Space'
        && this.modalStatus === 'editing'
        && !['INPUT', 'BUTTON', 'SUMMARY', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)
      ) {
        event.preventDefault()
        this.playSelection()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(this.$refs.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      ))
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    syncTimeInputs() {
      this.startInput = this.formatTimecode(this.draftStartSeconds)
      this.endInput = this.formatTimecode(this.draftEndSeconds)
    },
    markEdited() {
      this.forceEditing = true
      this.inputError = ''
      this.snapMessage = ''
      this.stopPreview()
    },
    snappedBoundary(value, bypassSnap = false) {
      if (!this.snapEnabled || bypassSnap) return { value, label: '' }
      if (!this.visibleCutTimes.length) return { value, label: '' }
      const nearest = this.visibleCutTimes.reduce((best, candidate) => (
        Math.abs(candidate - value) < Math.abs(best - value)
          ? candidate
          : best
      ))
      return Math.abs(nearest - value) <= SNAP_THRESHOLD_SECONDS
        ? { value: nearest, label: 'Camera cut' }
        : { value, label: '' }
    },
    snappedSelectionStart(value, duration, bypassSnap = false) {
      if (!this.snapEnabled || bypassSnap || !this.visibleCutTimes.length) {
        return { value, label: '' }
      }
      const boundaries = this.visibleCutTimes.flatMap(cut => [
        { value: cut, distance: Math.abs(cut - value) },
        { value: cut - duration, distance: Math.abs(cut - (value + duration)) },
      ])
      const nearest = boundaries.reduce((best, candidate) => (
        candidate.distance < best.distance ? candidate : best
      ))
      return nearest.distance <= SNAP_THRESHOLD_SECONDS
        ? { value: nearest.value, label: 'Camera cut' }
        : { value, label: '' }
    },
    moveDraftSelection(value, allowSnap = false, bypassSnap = false) {
      const duration = this.sectionDurationSeconds
      const minStart = Math.max(0, this.timelineStartSeconds)
      const maxStart = Math.min(
        this.sourceDurationSeconds - duration,
        this.timelineEndSeconds - duration
      )
      const rawStart = Math.round(Number(value))
      if (!Number.isFinite(rawStart) || maxStart < minStart) return
      const boundedStart = Math.max(minStart, Math.min(maxStart, rawStart))
      const snapped = allowSnap
        ? this.snappedSelectionStart(boundedStart, duration, bypassSnap)
        : { value: boundedStart, label: '' }
      const nextStart = Math.max(minStart, Math.min(maxStart, snapped.value))
      if (nextStart === this.draftStartSeconds) return
      this.markEdited()
      this.draftStartSeconds = nextStart
      this.draftEndSeconds = nextStart + duration
      this.playheadSeconds = nextStart
      this.syncTimeInputs()
      this.snapMessage = snapped.label
        ? `${snapped.label} · ${this.formatTimecode(nextStart)}`
        : ''
      this.seekExportPlayer(nextStart)
    },
    startSelectionDrag(event) {
      if (event.button !== 0 || this.sectionDurationSeconds >= this.sourceDurationSeconds) return
      const rail = this.$refs.filmstrip?.getBoundingClientRect()
      if (!rail?.width) return
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget.setPointerCapture?.(event.pointerId)
      this.focusedControl = 'selection'
      this.selectionDrag = {
        pointerId: event.pointerId,
        originX: event.clientX,
        originStart: this.draftStartSeconds,
        railWidth: rail.width,
      }
    },
    moveSelectionDrag(event) {
      if (!this.selectionDrag || event.pointerId !== this.selectionDrag.pointerId) return
      event.preventDefault()
      const timelineSpan = this.timelineEndSeconds - this.timelineStartSeconds
      const delta = (
        (event.clientX - this.selectionDrag.originX)
        / this.selectionDrag.railWidth
      ) * timelineSpan
      this.moveDraftSelection(
        this.selectionDrag.originStart + delta,
        true,
        event.altKey
      )
    },
    finishSelectionDrag(event) {
      if (!this.selectionDrag || event.pointerId !== this.selectionDrag.pointerId) return
      event.currentTarget.releasePointerCapture?.(event.pointerId)
      this.selectionDrag = null
    },
    onSelectionKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return
      event.preventDefault()
      const direction = event.key === 'ArrowLeft' ? -1 : 1
      const step = event.shiftKey ? 5 : 1
      this.moveDraftSelection(
        this.draftStartSeconds + (direction * step),
        true,
        event.altKey
      )
    },
    setDraftStart(value, allowSnap = false, bypassSnap = false) {
      const rawValue = Math.round(Number(value))
      if (!Number.isFinite(rawValue)) return
      const snapped = allowSnap
        ? this.snappedBoundary(rawValue, bypassSnap)
        : { value: rawValue, label: '' }
      this.markEdited()
      const next = snapped.value
      this.draftStartSeconds = Math.max(0, Math.min(next, this.draftEndSeconds - 1))
      this.syncTimeInputs()
      this.snapMessage = snapped.label
        ? `${snapped.label} · ${this.formatTimecode(this.draftStartSeconds)}`
        : ''
      this.seekExportPlayer(this.draftStartSeconds)
      this.reframeIfDraftOutsideTimeline()
    },
    setDraftEnd(value, allowSnap = false, bypassSnap = false) {
      const rawValue = Math.round(Number(value))
      if (!Number.isFinite(rawValue)) return
      const snapped = allowSnap
        ? this.snappedBoundary(rawValue, bypassSnap)
        : { value: rawValue, label: '' }
      this.markEdited()
      const next = snapped.value
      this.draftEndSeconds = Math.min(
        this.sourceDurationSeconds,
        Math.max(this.draftStartSeconds + 1, next)
      )
      this.syncTimeInputs()
      this.snapMessage = snapped.label
        ? `${snapped.label} · ${this.formatTimecode(this.draftEndSeconds)}`
        : ''
      this.seekExportPlayer(this.draftEndSeconds)
      this.reframeIfDraftOutsideTimeline()
    },
    setDraftStartFromRail(event) {
      this.setDraftStart(event.target.value, true, event.altKey)
    },
    setDraftEndFromRail(event) {
      this.setDraftEnd(event.target.value, true, event.altKey)
    },
    onHandleKeydown(event, boundary) {
      if (!event.shiftKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return
      event.preventDefault()
      const change = event.key === 'ArrowLeft' ? -5 : 5
      if (boundary === 'start') this.nudgeStart(change)
      else this.nudgeEnd(change)
    },
    commitStartInput() {
      const parsed = parseTimecode(this.startInput)
      if (parsed === null) {
        this.inputError = 'Enter the start as MM:SS or HH:MM:SS.'
        return
      }
      this.setDraftStart(parsed)
    },
    commitEndInput() {
      const parsed = parseTimecode(this.endInput)
      if (parsed === null) {
        this.inputError = 'Enter the end as MM:SS or HH:MM:SS.'
        return
      }
      this.setDraftEnd(parsed)
    },
    nudgeStart(seconds) {
      this.setDraftStart(this.draftStartSeconds + seconds)
    },
    nudgeEnd(seconds) {
      this.setDraftEnd(this.draftEndSeconds + seconds)
    },
    resetDraft() {
      this.draftStartSeconds = this.suggestedStartSeconds
      this.draftEndSeconds = this.suggestedEndSeconds
      this.playheadSeconds = this.suggestedStartSeconds
      this.syncTimeInputs()
      this.markEdited()
      this.snapMessage = 'Suggested range restored'
      this.seekExportPlayer(this.draftStartSeconds)
      this.reframeTimeline()
    },
    editClip() {
      this.forceEditing = true
      this.$nextTick(() => {
        this.seekExportPlayer(this.draftStartSeconds)
        this.loadFilmstrip()
      })
    },
    exportEvent() {
      if (!this.canExportSection) return
      this.stopPreview()
      this.forceEditing = false
      this.submittedStartSeconds = this.draftStartSeconds
      this.submittedEndSeconds = this.draftEndSeconds
      this.$emit('export-event', {
        exportKey: this.currentExportKey,
        ceremonyId: this.snapshotCeremonyId,
        eventId: this.clipEvent.id,
        event: this.clipEvent,
        startSeconds: this.draftStartSeconds,
        endSeconds: this.draftEndSeconds,
      })
    },
    exportPlayer() {
      return this.$refs.exportMediaPlayer?.player || null
    },
    onModalPlayerLoaded() {
      this.seekExportPlayer(this.draftStartSeconds)
    },
    async loadFilmstrip() {
      const requestId = ++this.filmstripRequestId
      this.filmstripFrames = []
      this.filmstripLoading = true
      try {
        const result = await getCeremonyFilmstrip(
          this.snapshotCeremonyId,
          this.timelineStartSeconds,
          this.timelineEndSeconds
        )
        if (requestId !== this.filmstripRequestId || !this.modalOpen) return
        this.filmstripFrames = Array.isArray(result.data?.frames)
          ? result.data.frames
          : []
      } catch {
        if (requestId === this.filmstripRequestId) this.filmstripFrames = []
      } finally {
        if (requestId === this.filmstripRequestId) this.filmstripLoading = false
      }
    },
    filmstripFrameStyle(frame) {
      const box = Array.isArray(frame.box) ? frame.box.map(Number) : []
      const columns = Math.max(1, Number(frame.sheet_columns) || 1)
      const rows = Math.max(1, Number(frame.sheet_rows) || 1)
      if (!frame.sprite_url || box.length !== 4 || !box[2] || !box[3]) return {}
      const column = Math.round(box[0] / box[2])
      const row = Math.round(box[1] / box[3])
      return {
        aspectRatio: `${box[2]} / ${box[3]}`,
        backgroundImage: `url(${frame.sprite_url})`,
        backgroundSize: `${columns * 100}% ${rows * 100}%`,
        backgroundPosition: `${columns > 1 ? (column / (columns - 1)) * 100 : 0}% ${
          rows > 1 ? (row / (rows - 1)) * 100 : 0
        }%`,
      }
    },
    seekFromFilmstrip(event) {
      if (event.target.closest('input, button')) return
      const rect = this.$refs.filmstrip?.getBoundingClientRect()
      if (!rect?.width) return
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
      this.setPlayhead(
        this.timelineStartSeconds
          + (ratio * (this.timelineEndSeconds - this.timelineStartSeconds))
      )
    },
    setPlayhead(value) {
      const next = Math.round(Number(value))
      if (!Number.isFinite(next)) return
      this.playheadSeconds = Math.max(
        this.timelineStartSeconds,
        Math.min(this.timelineEndSeconds, next)
      )
      this.seekExportPlayer(this.playheadSeconds, false)
    },
    seekExportPlayer(seconds, updatePlayhead = true) {
      if (updatePlayhead) this.playheadSeconds = seconds
      const player = this.exportPlayer()
      if (!player) return
      player.pause()
      player.currentTime(seconds)
    },
    playSelection() {
      const player = this.exportPlayer()
      if (!player) return
      if (this.isPreviewPlaying) {
        this.stopPreview()
        return
      }
      this.stopPreview()
      const playbackStart = Math.max(
        this.draftStartSeconds,
        Math.min(this.draftEndSeconds - 1, this.playheadSeconds)
      )
      this.playheadSeconds = playbackStart
      player.currentTime(playbackStart)
      player.on('timeupdate', this.stopPreviewAtEnd)
      this.isPreviewPlaying = true
      const promise = player.play()
      if (promise?.catch) {
        promise.catch(() => {
          this.isPreviewPlaying = false
        })
      }
    },
    stopPreviewAtEnd() {
      const player = this.exportPlayer()
      if (!player) return
      this.playheadSeconds = player.currentTime()
      if (player.currentTime() >= this.draftEndSeconds) {
        this.stopPreview()
        player.currentTime(this.draftStartSeconds)
        this.playheadSeconds = this.draftStartSeconds
      }
    },
    stopPreview() {
      const player = this.exportPlayer()
      this.isPreviewPlaying = false
      if (!player) return
      player.off('timeupdate', this.stopPreviewAtEnd)
      player.pause()
    },
  },
}
</script>

<style scoped>
.section-export-label {
  margin: 0 0 8px;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-export-copy {
  margin: 0 0 12px;
  color: #475569;
  font-size: 0.84rem;
  line-height: 1.45;
}

.section-export-button,
.footer-primary,
.footer-secondary,
.preview-button,
.time-input-row button {
  min-height: 44px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 700;
}

.section-export-button,
.footer-primary {
  border: 1px solid #111827;
  background: #111827;
  color: #fff;
}

.section-export-button:not(:disabled):hover,
.footer-primary:not(:disabled):hover {
  background: #1e293b;
}

.section-export-button:not(:disabled):active,
.footer-primary:not(:disabled):active {
  background: #020617;
}

.section-export-button {
  width: 100%;
  padding: 0 16px;
}

.section-export-button:disabled,
.footer-primary:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.section-export-button:focus-visible,
.footer-primary:focus-visible,
.footer-secondary:focus-visible,
.preview-button:focus-visible,
.reset-button:focus-visible,
.magnet-button:focus-visible,
.fine-tune summary:focus-visible,
.modal-close:focus-visible,
.time-input-row button:focus-visible,
.time-input-row input:focus-visible {
  outline: 3px solid rgba(124, 58, 237, 0.32);
  outline-offset: 2px;
}

.export-modal-backdrop {
  position: fixed;
  z-index: 5000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(10, 15, 29, 0.74);
  backdrop-filter: blur(6px);
}

.export-modal {
  display: flex;
  flex-direction: column;
  width: min(880px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(2, 6, 23, 0.34);
}

.export-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  min-height: 72px;
  padding: 14px 24px;
  border-bottom: 1px solid #e8ecf2;
}

.export-modal-header h2 {
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.15;
}

.export-modal-subtitle {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.81rem;
}

.modal-close {
  display: grid;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #334155;
  font-size: 1.3rem;
  line-height: 1;
}

.modal-close:hover {
  background: #f1f5f9;
}

.modal-close:active {
  background: #e2e8f0;
}

.export-modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px 22px;
}

.export-preview {
  width: min(760px, 100%);
  margin: 0 auto;
  overflow: hidden;
  border-radius: 12px;
  background: #0f172a;
}

.export-preview :deep(.video-stage),
.export-preview :deep(.ceremony-video-player) {
  border-radius: 12px;
  box-shadow: none;
}

.playback-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: min(760px, 100%);
  min-height: 48px;
  margin: 8px auto;
}

.playback-summary p {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 1px;
  margin: 0;
  color: #111827;
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
}

.clip-readout strong {
  font-size: 1.12rem;
  font-weight: 600;
}

.playback-summary p span {
  color: #64748b;
  font-size: 0.76rem;
}

.trim-filmstrip {
  position: relative;
  width: min(760px, calc(100% - 16px));
  height: 72px;
  margin: 0 auto;
  border: 1px solid #111827;
  border-radius: 8px;
  background: #111827;
  isolation: isolate;
}

.filmstrip-frames {
  position: absolute;
  z-index: 1;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(9, minmax(0, 1fr));
  overflow: hidden;
  border-radius: 8px;
}

.filmstrip-frame {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  background-color: #17132f;
  pointer-events: none;
}

.filmstrip-frame-image {
  position: absolute;
  top: 0;
  left: 50%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  pointer-events: none;
  transform: translateX(-50%);
}

.filmstrip-frames.loading {
  background: linear-gradient(100deg, #17132f 25%, #292143 45%, #17132f 65%);
  background-size: 200% 100%;
  animation: filmstrip-loading 1.2s ease-in-out infinite;
}

.filmstrip-empty {
  position: absolute;
  z-index: 3;
  inset: 0;
  display: grid;
  place-items: center;
  margin: 0;
  background: #111827;
  color: #cbd5e1;
  font-size: 0.78rem;
  pointer-events: none;
}

.filmstrip-mask {
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  background: rgba(2, 6, 23, 0.58);
  pointer-events: none;
}

.filmstrip-mask-left {
  left: 0;
}

.filmstrip-mask-right {
  right: 0;
}

.cut-marker {
  position: absolute;
  z-index: 3;
  top: auto;
  bottom: 5px;
  height: 9px;
  width: 1px;
  background: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.trim-selection {
  position: absolute;
  z-index: 4;
  top: 0;
  bottom: 0;
  border: 2px solid #f5b82e;
  border-radius: 4px;
  pointer-events: none;
}

.selection-drag-target {
  position: absolute;
  z-index: 1;
  inset: 2px 8px;
  display: grid;
  overflow: hidden;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  cursor: grab;
  pointer-events: auto;
  touch-action: none;
  place-items: center;
}

.selection-drag-target:active {
  cursor: grabbing;
}

.selection-grip {
  display: grid;
  overflow: hidden;
  width: 28px;
  max-width: calc(100% - 4px);
  height: 20px;
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.82);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.28);
  place-items: center;
}

.selection-grip svg {
  width: 14px;
  height: 14px;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.5;
}

.trim-selection.dragging,
.trim-selection.focus-selection {
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.36);
}

.trim-handle {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 12px;
  background: #f5b82e;
}

.trim-handle::before {
  position: absolute;
  top: calc(50% - 7px);
  width: 1px;
  height: 14px;
  border-radius: 1px;
  background: rgba(17, 24, 39, 0.6);
  content: '';
}

.trim-handle::before {
  left: 5px;
}

.trim-handle-start {
  left: -6px;
  border-radius: 4px 0 0 4px;
}

.trim-handle-end {
  right: -6px;
  border-radius: 0 4px 4px 0;
}

.filmstrip-playhead {
  position: absolute;
  z-index: 6;
  top: -4px;
  bottom: -4px;
  width: 1px;
  background: #fff;
  pointer-events: none;
  transform: translateX(-0.5px);
}

.filmstrip-playhead::before {
  position: absolute;
  top: 0;
  left: -3px;
  width: 7px;
  height: 4px;
  border-radius: 2px;
  background: #fff;
  content: '';
}

.filmstrip-playhead.focused {
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.42);
}

.trim-selection.focus-start .trim-handle-start,
.trim-selection.focus-end .trim-handle-end {
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.42);
}

.trim-range,
.playhead-range {
  position: absolute;
  z-index: 7;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  pointer-events: none;
  appearance: none;
  background: transparent;
}

.playhead-range {
  z-index: 5;
}

.trim-range::-webkit-slider-thumb {
  width: 44px;
  height: 72px;
  border: 0;
  background: transparent;
  cursor: ew-resize;
  pointer-events: auto;
  appearance: none;
}

.playhead-range::-webkit-slider-thumb {
  width: 24px;
  height: 72px;
  border: 0;
  background: transparent;
  cursor: ew-resize;
  pointer-events: auto;
  appearance: none;
}

.trim-range::-moz-range-thumb {
  width: 44px;
  height: 72px;
  border: 0;
  background: transparent;
  cursor: ew-resize;
  pointer-events: auto;
}

.playhead-range::-moz-range-thumb {
  width: 24px;
  height: 72px;
  border: 0;
  background: transparent;
  cursor: ew-resize;
  pointer-events: auto;
}

.trim-filmstrip:focus-within {
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.22);
}

.filmstrip-scale {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: min(760px, calc(100% - 16px));
  min-height: 22px;
  margin: 0 auto;
  color: #94a3b8;
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
}

.ceremony-overview {
  width: min(760px, calc(100% - 16px));
  margin: 6px auto 0;
}

.ceremony-overview-track {
  position: relative;
  height: 3px;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
}

.ceremony-overview-window {
  position: absolute;
  top: 0;
  bottom: 0;
  min-width: 4px;
  border-radius: 999px;
  background: #64748b;
}

.ceremony-overview-scale {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 5px;
  color: #94a3b8;
  font-size: 0.66rem;
  font-variant-numeric: tabular-nums;
}

.ceremony-overview-scale span {
  color: #64748b;
}

.snap-message {
  color: #475569;
  font-weight: 600;
}

.trim-hint {
  color: #64748b;
}

.trim-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: min(760px, 100%);
  margin: 2px auto 0;
}

.magnet-button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 36px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #475569;
  font-size: 0.74rem;
  font-weight: 600;
}

.magnet-icon {
  width: 15px;
  height: 15px;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
}

.magnet-button.active {
  border-color: #cbd5e1;
  background: #f1f5f9;
  color: #1e293b;
}

.fine-tune {
  width: min(760px, 100%);
  margin: 10px auto 0;
  border-top: 1px solid #e8ecf2;
}

.fine-tune summary {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: 0 2px;
  color: #475569;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  justify-content: space-between;
  list-style: none;
}

.fine-tune summary::-webkit-details-marker {
  display: none;
}

.fine-tune summary::after {
  width: 7px;
  height: 7px;
  margin-right: 6px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  content: '';
  transform: rotate(45deg);
  transition: transform 140ms ease;
}

.fine-tune[open] summary::after {
  transform: rotate(225deg);
}

.fine-tune > .magnet-button {
  margin-top: 10px;
}

.trim-controls {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 8px;
}

.time-control {
  min-width: 0;
  margin: 0;
  padding: 8px;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  background: #f8fafc;
}

.time-control legend {
  padding: 0 6px;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.time-input-row {
  display: grid;
  grid-template-columns: auto minmax(92px, 1fr) auto;
  gap: 8px;
}

.time-input-row button,
.time-input-row input {
  min-width: 0;
  border: 1px solid #dbe2ea;
  background: #fff;
}

.time-input-row button {
  padding: 0 12px;
  color: #334155;
}

.time-input-row input {
  width: 100%;
  border-radius: 8px;
  color: #111827;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  text-align: center;
}

.section-export-error {
  margin: 14px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fef2f2;
  color: #b42318;
  font-size: 0.82rem;
}

.preview-button,
.reset-button,
.footer-secondary {
  border: 1px solid #dbe2ea;
  background: #fff;
  color: #334155;
}

.preview-button:hover,
.footer-secondary:hover,
.time-input-row button:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.preview-button:active,
.footer-secondary:active,
.time-input-row button:active {
  background: #f1f5f9;
}

.preview-button,
.reset-button {
  padding: 0 14px;
}

.preview-button span {
  margin-right: 6px;
}

.reset-button {
  min-height: 34px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 600;
}

.export-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 24px;
  border-top: 1px solid #e8ecf2;
  background: #fff;
}

.footer-primary,
.footer-secondary {
  padding: 0 18px;
}

.export-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  flex-direction: column;
  min-height: 430px;
  padding: 42px 24px;
  text-align: center;
}

.export-state h3 {
  margin: 18px 0 8px;
  color: #111827;
  font-size: 1.4rem;
}

.export-state > p {
  max-width: 480px;
  margin: 0 0 18px;
  color: #64748b;
  line-height: 1.55;
}

.export-progress,
.export-success,
.export-failure {
  display: grid;
  width: 56px;
  height: 56px;
  place-items: center;
  border-radius: 50%;
}

.export-progress {
  border: 4px solid #ddd6fe;
  border-top-color: #7c3aed;
  animation: export-spin 0.9s linear infinite;
}

.export-success {
  background: #dcfce7;
  color: #15803d;
  font-size: 1.45rem;
  font-weight: 800;
}

.export-failure {
  background: #fee2e2;
  color: #b42318;
  font-size: 1.35rem;
  font-weight: 800;
}

.export-range-card {
  display: grid;
  min-width: min(360px, 100%);
  gap: 4px;
  margin: 0 0 22px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
  color: #111827;
}

.export-range-card span,
.export-preserved {
  color: #64748b;
  font-size: 0.82rem;
}

.export-state-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.download-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

@keyframes export-spin {
  to { transform: rotate(360deg); }
}

@keyframes filmstrip-loading {
  to { background-position: -200% 0; }
}

@media (max-width: 640px) {
  .export-modal-backdrop {
    align-items: end;
    padding: 24px 0 0;
  }

  .export-modal {
    width: 100%;
    max-height: calc(100dvh - 24px);
    border-right: 0;
    border-bottom: 0;
    border-left: 0;
    border-radius: 18px 18px 0 0;
  }

  .export-modal-header {
    min-height: 60px;
    padding: 12px 16px;
  }

  .export-modal-header h2 {
    font-size: 1.25rem;
  }

  .modal-close {
    margin-top: -4px;
  }

  .export-modal-body {
    padding: 16px;
  }

  .playback-summary {
    align-items: center;
  }

  .playback-summary p {
    align-items: flex-end;
    flex-direction: column;
    gap: 1px;
  }

  .ceremony-overview {
    width: calc(100% - 20px);
  }

  .trim-filmstrip {
    width: calc(100% - 16px);
    height: 64px;
  }

  .trim-range::-webkit-slider-thumb,
  .playhead-range::-webkit-slider-thumb {
    height: 64px;
  }

  .trim-range::-moz-range-thumb,
  .playhead-range::-moz-range-thumb {
    height: 64px;
  }

  .trim-controls {
    grid-template-columns: 1fr;
  }

  .export-modal-footer {
    position: sticky;
    bottom: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr);
    padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  }

  .footer-primary,
  .footer-secondary {
    width: 100%;
  }
}

@media (min-width: 901px) and (max-height: 760px) {
  .export-preview,
  .export-preview :deep(.video-stage),
  .export-preview :deep(.ceremony-video-player) {
    height: 30vh;
    min-height: 180px;
    max-height: 220px;
    aspect-ratio: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .export-progress {
    animation: none;
  }

  .filmstrip-frames.loading {
    animation: none;
  }
}
</style>
